import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Text, Flex, Container, Table, TextField, Dialog } from '@radix-ui/themes';
import api from '../../api/axios.js';

const UsersList = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const filtered = users.filter(user => {
            const searchLower = searchTerm.toLowerCase();
            return (
                user.username.toLowerCase().includes(searchLower) ||
                `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchLower) ||
                user.position.toLowerCase().includes(searchLower) ||
                (user.Branch?.name || '').toLowerCase().includes(searchLower) ||
                (user.Team?.name || '').toLowerCase().includes(searchLower) ||
                (user.is_admin ? 'admin' : 'user').includes(searchLower)
            );
        });
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        try {
            const response = await api.get(`/users`);
            setUsers(response.data);
            setFilteredUsers(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const handleUserDoubleClick = (userId) => {
        navigate(`/progress/${userId}`);
    };

    const generateRandomPassword = () => {
        const length = 8;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let password = "";
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charset.length);
            password += charset[randomIndex];
        }
        return password;
    };

    const handleUserChangePassword = async (userId) => {
        try {
            const randomPassword = generateRandomPassword();
            const response = await api.put(
                `/users/${userId}/password`,
                { password: randomPassword }
            );
            
            if (response.status === 200) {
                const user = users.find(u => u.id === userId);
                setCurrentPassword(randomPassword);
                setCurrentUser(user);
                setShowPasswordDialog(true);
            }
        } catch (error) {
            console.error('Error updating password:', error);
            alert('Failed to update password. Please try again.');
        }
    };

    const handleUserDelete = async (userId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this user?');
        if (!confirmDelete) return;

        try {
            const response = await api.put(
                `/users/${userId}/delete`
            );
            
            if (response.status === 200) {
                // Remove user from state (assume you have setUsers and users list)
                setUsers(users => users.filter(user => user.id !== userId));
            }
        } catch (error) {
            console.error('Error delete user:', error);
            alert('Failed to delete user. Please try again.');
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(currentPassword);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container style={{ 
            width: '100vw', 
            height: '100vh', 
            padding: 0, 
            margin: 0, 
            maxWidth: '100vw' 
        }}>
            <Box style={{ padding: '2rem' }}>
                <Flex justify="between" align="center" mb="4">
                    <Text size="6" weight="bold">จัดการรหัสผ่านพนักงาน</Text>
                    <Flex gap="2">
                        <TextField.Root>
                            <TextField.Slot>
                                <input 
                                    placeholder="ค้นหาพนักงาน..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        padding: '8px'
                                    }}
                                />
                            </TextField.Slot>
                        </TextField.Root>
                    </Flex>
                </Flex>

                <Table.Root>
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeaderCell>Username</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Position</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Branch</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Team</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Last Update</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {filteredUsers.map((user) => (
                            <Table.Row 
                                key={user.id} 
                                onDoubleClick={() => handleUserDoubleClick(user.id)}
                                style={{ 
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'var(--gray-a2)'
                                    }
                                }}
                            >
                                <Table.Cell>{user.username}</Table.Cell>
                                <Table.Cell>{`${user.firstname} ${user.lastname}`}</Table.Cell>
                                <Table.Cell>{user.position}</Table.Cell>
                                <Table.Cell>{user.Branch?.name || '-'}</Table.Cell>
                                <Table.Cell>{user.Team?.name || '-'}</Table.Cell>
                                <Table.Cell>{user.is_admin ? 'Admin' : 'User'}</Table.Cell>
                                <Table.Cell>{new Date(user.update_at).toLocaleDateString()}</Table.Cell>
                                <Table.Cell>
                                    <Button style={{ cursor: 'pointer', width: 'max-content' }} variant="outline" color="gray" onClick={() => handleUserChangePassword(user.id)}>
                                        Change Password
                                    </Button>
                                    <Button style={{ cursor: 'pointer', width: 'max-content' }} variant="outline" color="red" onClick={() => handleUserDelete(user.id)}>
                                        Delete User
                                    </Button>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>

            <Dialog.Root open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <Dialog.Content style={{ maxWidth: 450 }}>
                    <Dialog.Title>Password Updated Successfully</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        New password has been set for:
                        <Text weight="bold" style={{ display: 'block', marginTop: '0.5rem' }}>
                            {currentUser?.username}
                        </Text>
                        <Text color="gray" size="2">
                            {currentUser?.firstname} {currentUser?.lastname}
                        </Text>
                    </Dialog.Description>

                    <Box style={{ 
                        backgroundColor: 'var(--gray-a2)', 
                        padding: '1rem',
                        borderRadius: 'var(--radius-3)',
                        marginBottom: '1rem'
                    }}>
                        <Text size="4" weight="bold" style={{ 
                            fontFamily: 'monospace',
                            letterSpacing: '0.1em'
                        }}>
                            {currentPassword}
                        </Text>
                    </Box>

                    <Flex gap="3" mt="4" justify="end">
                        <Button variant="soft" color="gray" onClick={() => setShowPasswordDialog(false)}>
                            Close
                        </Button>
                        <Button onClick={copyToClipboard}>
                            Copy Password
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </Container>
    );
};

export default UsersList;
